import { useMemo, useState } from 'react';
import PageHeader from '@/components/feature/PageHeader';
import ContentCard from '@/components/feature/ContentCard';
import EmptyState from '@/components/base/EmptyState';
import { useSEO } from '@/hooks/useSEO';
import { videoItems } from '@/hooks/useContent';

const VIDEO_TYPES = ['全部', '电影剪辑', '综艺', '短视频'];
const DURATIONS = [
  { key: 'all', label: '全部' },
  { key: 'short', label: '短于 5 分钟' },
  { key: 'medium', label: '5 - 20 分钟' },
];
const INITIAL = 6;
const STEP = 3;

function toSeconds(duration: string): number {
  const parts = duration.split(':').map((value) => Number(value));
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  return 0;
}

export default function VideoCategoryPage() {
  const [videoType, setVideoType] = useState('全部');
  const [duration, setDuration] = useState('all');
  const [visible, setVisible] = useState(INITIAL);

  useSEO(
    '视频 - 一起艹',
    '精彩影视剪辑、综艺与短视频集合，支持按类型与时长筛选，发现下一段让你停不下来的内容。',
    '娱乐视频,影视剪辑,综艺,短视频,一起艹',
  );

  const filtered = useMemo(() => {
    return videoItems.filter((item) => {
      const typeOk = videoType === '全部' || item.videoType === videoType;
      const seconds = toSeconds(item.duration);
      const durationOk =
        duration === 'all' ||
        (duration === 'short' && seconds < 300) ||
        (duration === 'medium' && seconds >= 300 && seconds <= 1200);
      return typeOk && durationOk;
    });
  }, [videoType, duration]);

  const shown = filtered.slice(0, visible);

  const reset = () => {
    setVideoType('全部');
    setDuration('all');
    setVisible(INITIAL);
  };

  return (
    <div className="w-full">
      <PageHeader
        title="精彩视频"
        subtitle="精彩影视、综艺、短视频，按类型与时长筛选，总有一条适合现在的你。"
        crumbs={[{ label: '首页', to: '/' }, { label: '视频' }]}
        icon="ri-play-circle-line"
      />

      <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-8 md:py-12">
        <div className="flex flex-col gap-4 mb-6 md:mb-8 p-4 md:p-5 rounded-lg border border-background-200 bg-background-50">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            <span className="text-xs md:text-sm text-foreground-400 sm:w-16 shrink-0">类型</span>
            <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 w-fit overflow-x-auto">
              {VIDEO_TYPES.map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => {
                    setVideoType(type);
                    setVisible(INITIAL);
                  }}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                    videoType === type
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-primary-600'
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            <span className="text-xs md:text-sm text-foreground-400 sm:w-16 shrink-0">时长</span>
            <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 w-fit overflow-x-auto">
              {DURATIONS.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => {
                    setDuration(option.key);
                    setVisible(INITIAL);
                  }}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                    duration === option.key
                      ? 'bg-secondary-700 text-background-50'
                      : 'text-foreground-600 hover:text-secondary-700'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {filtered.length === 0 ? (
          <EmptyState
            icon="ri-play-circle-line"
            title="暂无符合条件的视频"
            description="当前筛选条件下没有找到视频，换个类型或时长试试，或重置筛选条件。"
            actionLabel="重置筛选"
            onAction={reset}
          />
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {shown.map((item) => (
                <ContentCard key={item.id} item={item} />
              ))}
            </div>
            {visible < filtered.length ? (
              <div className="flex justify-center mt-8 md:mt-10">
                <button
                  type="button"
                  onClick={() => setVisible((prev) => prev + STEP)}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-secondary-700 text-background-50 text-sm font-medium hover:bg-secondary-600 whitespace-nowrap cursor-pointer transition-colors"
                >
                  <i className="ri-add-line" />
                  加载更多视频
                </button>
              </div>
            ) : (
              <p className="text-center text-xs text-foreground-400 mt-8">已经到底啦，共 {filtered.length} 条视频</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}